/**
 * Previous / page numbers / Next, with a "Showing 1–25 of 132" line.
 *
 * Controlled: the caller owns `page` (here, the URL) and this only reports a
 * request to change it. Page numbers are windowed — the first, the last, and the
 * current page with one neighbour either side, gaps shown as an ellipsis — so a
 * catalog of two hundred pages does not render two hundred buttons.
 */
import { pageWindow } from './page-window';

export function Pagination(
  { page, pageSize, total, onPage }: {
    page: number;
    pageSize: number;
    total: number;
    onPage: (page: number) => void;
  },
) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);

  return (
    <nav className="mk-pager" aria-label="Pagination">
      <span className="mk-pager-count">
        {total === 0 ? 'No products' : `Showing ${first}–${last} of ${total}`}
      </span>
      {pageCount > 1 ? (
        <div className="mk-pager-nav">
          <button type="button" className="mk-pager-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            Previous
          </button>
          {pageWindow(page, pageCount).map((p, i) => (p === null ? (
            <span key={`gap-${i}`} className="mk-pager-gap" aria-hidden="true">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className="mk-pager-btn"
              aria-current={p === page ? 'page' : undefined}
              aria-label={`Page ${p}`}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          )))}
          <button type="button" className="mk-pager-btn" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>
            Next
          </button>
        </div>
      ) : null}
    </nav>
  );
}
